import { ApolloProvider } from '@apollo/client';
import * as Sentry from '@sentry/react';
import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import { createBrowserRouter, Navigate, RouteObject, RouterProvider } from 'react-router';
import { CreateApolloClient } from './api/client/apollo';
import App from './App';
import { FullScreenLoading } from './components/displays/FullScreenLoading';
import './i18n';
import Layout from './layouts/dashboard';
import AccountPage from './pages/Account/Account.page';
import ClassesPage from './pages/Classes/Classes.page';
import ClassroomPage from './pages/Classroom/Classroom.page';
import HomeworkPage from './pages/Homework/Homework.page';
import LessonEditorPage from './pages/LessonEditor/LessonEditor.page';
import LessonsPage from './pages/Lessons/Lessons.page';
import OnboardingPage from './pages/Onboarding/Onboarding.page';
import PlaygroundPage from './pages/Playground/Playground.page';
import ResetPasswordPage from './pages/ResetPassword/ResetPassword.page';
import ResultsClassroomPage from './pages/Results/Results.classroom.page';
import ResultsHomeworkPage from './pages/Results/Results.homework.page';
import { ResultsPage } from './pages/Results/Results.page';
import { StudentsPage } from './pages/Students/Students.page';
import WelcomePage from './pages/Welcome/Welcome.page';
import { PreferenceProvider } from './providers/preference/PreferenceProvider';
import { SessionProvider, useSession } from './providers/session/SessionProvider';

// Component to handle role-based home redirection
const Home = () => {
    const { session } = useSession();
    const userRole = session?.user?.role;

    if (userRole === 'student') {
        return <Navigate to='/account' replace />;
    }

    return <Navigate to='/lessons' replace />;
};

const DevRoutes: RouteObject[] =
    import.meta.env.MODE === 'development'
        ? [
              {
                  path: '/lab/playground',
                  Component: PlaygroundPage,
              },
              {
                  path: '/lab/sentry',
                  Component: React.lazy(() => import('./pages/Playground/Sentry.page')),
              },
              {
                  path: '/lab/google-classroom',
                  Component: React.lazy(() => import('./pages/Playground/GoogleClasssroom.page')),
              },
              {
                  path: '/ai-grader/rubrics',
                  Component: React.lazy(() => import('./pages/Playground/AIGrader/AIGrader.rubric.page')),
              },
              {
                  path: '/ai-grader/assignments',
                  Component: React.lazy(() => import('./pages/Playground/AIGrader/AIGrader.list.page')),
              },
              {
                  path: '/ai-grader/assignments/:id',
                  Component: React.lazy(() => import('./pages/Playground/AIGrader/AIGrader.result.page')),
              },
          ]
        : [];

const router = createBrowserRouter([
    {
        Component: App,
        children: [
            /**
             * Private pages
             */
            {
                path: '/',
                Component: Layout,
                children: [
                    {
                        path: '/',
                        Component: Home, // Redirect to lesson page
                    },
                    {
                        path: '/lessons',
                        Component: LessonsPage,
                    },
                    {
                        path: '/lessons/:lessonId',
                        Component: LessonEditorPage,
                    },
                    {
                        path: '/students',
                        Component: StudentsPage,
                    },
                    {
                        path: '/account',
                        Component: AccountPage,
                    },
                    {
                        path: '/classes',
                        Component: ClassesPage,
                    },
                    {
                        path: '/homework',
                        Component: HomeworkPage,
                    },
                    {
                        path: '/classrooms/:classroomId',
                        Component: ClassroomPage,
                    },
                    {
                        path: '/results',
                        Component: ResultsPage,
                    },
                    {
                        path: '/results/classrooms/:classroomId',
                        Component: ResultsClassroomPage,
                    },
                    {
                        path: '/results/homeworks/:homeworkId',
                        Component: ResultsHomeworkPage,
                    },
                    ...DevRoutes,
                ],
            },
            /**
             * Public pages
             */
            {
                path: '/welcome',
                Component: WelcomePage,
            },
            {
                path: '/onboarding',
                Component: OnboardingPage,
            },
            {
                path: '/reset',
                Component: ResetPasswordPage,
            },
        ],
    },
]);

Sentry.init({
    dsn: 'https://0ceb9879c6e88bda7c691b3c50f9bf56@o1081309.ingest.us.sentry.io/4510300192768000',
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [Sentry.replayIntegration()],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10% of all sessions.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    environment: import.meta.env.MODE || undefined,
});

const client = CreateApolloClient();
const root = ReactDOM.createRoot(document.getElementById('root')!, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
});
root.render(
    <React.StrictMode>
        <ApolloProvider client={client}>
            <PreferenceProvider>
                <SessionProvider loadingIndicator={<FullScreenLoading />}>
                    <RouterProvider router={router} />
                </SessionProvider>
            </PreferenceProvider>
        </ApolloProvider>
    </React.StrictMode>,
);
